import { Link } from 'react-router-dom';
import { usePersonActivity } from '../../../hooks/usePeople';
import type { PersonActivityItem } from '../../../lib/peopleApi';
import { RelativeTime } from '../../ui/RelativeTime';
import { describeActivity } from '../../../lib/taskActivityText';
import { ActivityDetail } from '../../tasks/linear/ActivityDetail';

/**
 * Activity tab for the Team Member detail page (CLE-241, CLE-237 epic).
 *
 * Renders a chronological feed of TaskActivity events across every task
 * assigned to the person. Mirrors the per-task activity log
 * (DetailActivity) — a minimal dot + human-readable action text + relative
 * timestamp — but adapted to the People detail page's light theme and linked
 * to each task's detail page when the task belongs to a project.
 */

function taskDetailPath(task: PersonActivityItem['task']): string | null {
  const ref = task.identifier ?? task.id;
  if (!ref) return null;
  // CLE-274: canonical project-independent task route.
  return `/tasks/${encodeURIComponent(ref)}`;
}

function ActivityRow({ item }: { item: PersonActivityItem }) {
  const path = taskDetailPath(item.task);
  const taskLabel = item.task.identifier ?? item.task.title;
  const { text, detail } = describeActivity(item);

  return (
    <li className="flex items-start gap-3">
      <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-gray-300" />
      <div className="min-w-0">
        <p className="text-sm text-gray-800">
          {item.user?.name ?? 'Cineva'} {text}
          {' pe '}
          {path ? (
            <Link to={path} className="font-medium text-brand-700 hover:underline">
              {taskLabel}
            </Link>
          ) : (
            <span className="font-medium text-gray-900">{taskLabel}</span>
          )}
        </p>
        {detail && <ActivityDetail detail={detail} />}
        <p className="text-xs text-gray-400">
          {item.task.projectName ? `${item.task.projectName} · ` : ''}
          <RelativeTime iso={item.createdAt} />
        </p>
      </div>
    </li>
  );
}

export interface PersonActivityTabProps {
  personId: string;
}

export function PersonActivityTab({ personId }: PersonActivityTabProps) {
  const { data, isLoading, isError } = usePersonActivity(personId);
  const items = data?.items ?? [];

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <h2 className="font-semibold text-gray-800">Activitate</h2>

      {isLoading ? (
        <p className="mt-4 text-sm text-gray-500">Se încarcă activitatea…</p>
      ) : isError ? (
        <p className="mt-4 text-sm text-red-600">Încărcarea activității a eșuat.</p>
      ) : items.length === 0 ? (
        <p className="mt-4 text-sm text-gray-500">
          Încă nu există activitate pentru acest membru al echipei.
        </p>
      ) : (
        <ul className="mt-3 space-y-3">
          {items.map((item) => (
            <ActivityRow key={item.id} item={item} />
          ))}
        </ul>
      )}
    </section>
  );
}
